import React, { useEffect, useState } from 'react';
import { Form, Checkbox, Input, Modal, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { addXgl, updateXgl } from 'src/api/api';
import { useAppSelector } from 'src/redux/hooks';

interface XglModalProps {
  open: boolean;
  onClose: () => void;
  xgl: API.DimensionItem | null;
  onSubmit: () => void;
}

const XglModal: React.FC<XglModalProps> = ({
  open,
  onClose,
  xgl,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const clientData = useAppSelector((state) => state.client.selectedClientData);
  const [form] = Form.useForm();
  const isAdd = !xgl;
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (xgl) {
      form.setFieldsValue({
        description: xgl.description,
        code: xgl.code,
        active: xgl.active,
      });
    } else {
      form.resetFields();
    }
  }, [xgl]);

  const handleFormSubmit = async (values: any) => {
    const data: API.DimensionItem = {
      ...xgl,
      uid: '',
      client_id: clientData.id,
      description: values.description,
      code: values.code,
      active: values.active,
      admins: clientData.admins,
      users: clientData.users,
    };
    setLoading(true);
    try {
      if (isAdd) {
        await addXgl(accessToken, data);
      } else {
        await updateXgl(accessToken, data);
      }
      notification.success({
        message: t('Success'),
        description: t(isAdd ? 'itemAdded' : 'itemUpdated', { item: 'XGL' }),
        placement: 'topRight',
        duration: 4.5,
      });
      onClose();
      onSubmit();
    } catch (error: any) {
      notification.error({
        message: t('Error'),
        description: `${isAdd ? t('Failed to add') : t('Failed to update')} XGL, ${t('please try again.')}`,
        placement: 'topRight',
        duration: 4.5,
      });
      console.error(error);
      if (error.status === 409) {
        form.setFields([
          {
            name: 'code',
            errors: [error.response.data.detail],
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields(); // Validate the form
      handleFormSubmit(values); // If valid, submit the form
    } catch (errorInfo) {}
  };

  return (
    <Modal
      title={`${isAdd ? t('Add') : t('Edit')} XGL`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      destroyOnClose
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={t('Description')}
          name="description"
          rules={[{ required: true, message: t('Please enter description') }]}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          label={t('Code')}
          name="code"
          rules={[{ required: true, message: t('Please enter code') }]}
        >
          <Input maxLength={20} />
        </Form.Item>
        <Form.Item name="active" valuePropName="checked" initialValue={true}>
          <Checkbox>{t('Is XGL active?')}</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default XglModal;
